import { useEffect, useState } from 'react'
import {
  AttemptInfo,
  CourseRecommendationsResult,
  PublicQuizAPI,
} from '../apis/PublicQuizAPI'
import logo from '../images/unsw.jpg'

interface AssessmentReportCoursesProps {
  attemptId: string
}

function AssessmentReportCourses({ attemptId }: AssessmentReportCoursesProps) {
  const [attemptInfo, setAttemptInfo] = useState<AttemptInfo | null>(null)
  const [recommendations, setRecommendations] =
    useState<CourseRecommendationsResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true)
        setError(null)

        const [info, courses] = await Promise.all([
          PublicQuizAPI.getAttemptInfo(attemptId),
          PublicQuizAPI.getCourseRecommendations(attemptId),
        ])

        setAttemptInfo(info)
        setRecommendations(courses)

        if (
          info.resultCode &&
          !new URLSearchParams(window.location.search).has('resultCode')
        ) {
          const params = new URLSearchParams(window.location.search)
          params.delete('attemptId')
          params.set('resultCode', info.resultCode)
          window.history.replaceState(
            null,
            '',
            `${window.location.pathname}?${params.toString()}`,
          )
        }
      } catch (err) {
        console.error('Failed to load course recommendations', err)
        setError('Could not load recommended courses.')
      } finally {
        setLoading(false)
      }
    }

    if (attemptId) loadData()
  }, [attemptId])

  const resultCode =
    attemptInfo?.resultCode ?? recommendations?.resultCode ?? attemptId
  const reportParams = new URLSearchParams(window.location.search)
  reportParams.delete('page')
  reportParams.delete('attemptId')
  reportParams.set('v', '3')
  reportParams.set('resultCode', resultCode)
  const reportHref = `/?${reportParams.toString()}`

  const primaryAreaGroups = recommendations?.primaryByArea ?? []
  const primaryUrls = new Set(
    primaryAreaGroups.flatMap((area) =>
      area.competences.flatMap((c) => c.courses.map((course) => course.url)),
    ),
  )
  const fallbackGroups = (recommendations?.fallbackByArea ?? [])
    .map((group) => ({
      ...group,
      courses: group.courses.filter((course) => !primaryUrls.has(course.url)),
    }))
    .filter((group) => group.courses.length > 0)

  if (loading) {
    return (
      <div className="min-h-screen bg-white px-6 py-10 text-black">
        <div className="mx-auto max-w-6xl pb-4">
          <img src={logo} alt="UNSW logo" className="w-[220px]" />
        </div>
        <div className="mx-auto max-w-6xl rounded-xl border border-gray-300 p-10">
          <div
            className="text-base font-semibold"
            style={{ fontFamily: 'Clancy, Roboto, Arial, sans-serif' }}
          >
            Loading course recommendations...
          </div>
        </div>
      </div>
    )
  }

  if (error || !recommendations) {
    return (
      <div className="min-h-screen bg-white px-6 py-10 text-black">
        <div className="mx-auto max-w-6xl pb-4">
          <img src={logo} alt="UNSW logo" className="w-[220px]" />
        </div>
        <div className="mx-auto max-w-6xl rounded-xl border border-gray-300 p-10">
          <h1
            className="text-[32px] font-bold leading-tight"
            style={{ fontFamily: 'Clancy, Roboto, Arial, sans-serif' }}
          >
            Recommended Courses
          </h1>
          <p className="mt-4 text-sm text-red-600">{error ?? 'Insufficient data.'}</p>
          <a
            className="mt-8 inline-flex items-center justify-center gap-2 border-2 px-4 py-2 hover:brightness-95 active:scale-[0.99] transition-all"
            style={{
              backgroundColor: 'rgb(255, 220, 0)',
              color: 'rgb(51, 51, 51)',
              borderColor: 'rgb(51, 51, 51)',
              fontFamily: 'RobotoMono, Arial, sans-serif',
              fontSize: 16,
              fontWeight: 400,
            }}
            href={reportHref}
          >
            Back to report
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </a>
        </div>
      </div>
    )
  }

  return (
    <div
      className="min-h-screen bg-white px-6 py-10 text-black"
      style={{ fontFamily: 'Roboto, Arial, sans-serif' }}
    >
      <div className="mx-auto max-w-6xl pb-4">
        <img src={logo} alt="UNSW logo" className="w-[220px]" />
      </div>
      <div className="mx-auto flex max-w-6xl flex-col gap-8">
        <section className="py-2">
          <div className="space-y-5 text-[15px] text-gray-900">
            <div>
              <div
                className="text-[32px] font-bold leading-tight text-black"
                style={{ fontFamily: 'Clancy, Roboto, Arial, sans-serif' }}
              >
                Candidate
              </div>
            </div>

            <div className="rounded-xl border border-gray-300 p-6">
              <div className="text-[18px] font-semibold leading-snug">
                {attemptInfo?.candidate?.name || 'Unknown'}
              </div>

              <div className="mt-4">
                <div
                  className="text-[11px] uppercase tracking-[0.18em] text-gray-600"
                  style={{ fontFamily: 'RobotoMono, Arial, sans-serif' }}
                >
                  Result code
                </div>
                <div className="mt-1 break-all text-[14px] font-semibold">{resultCode}</div>
              </div>

              <div className="mt-4">
                <div
                  className="text-[11px] uppercase tracking-[0.18em] text-gray-600"
                  style={{ fontFamily: 'RobotoMono, Arial, sans-serif' }}
                >
                  Thresholds
                </div>
                <div className="mt-1 text-[14px] leading-6">
                  competence &lt; {recommendations.thresholds.competenceScoreLt},
                  area &lt; {recommendations.thresholds.areaScoreLt}
                </div>
              </div>

              <a
                className="mt-5 inline-flex items-center justify-center gap-2 border-2 px-4 py-2 hover:brightness-95 active:scale-[0.99] transition-all"
                style={{
                  backgroundColor: 'rgb(255, 220, 0)',
                  color: 'rgb(51, 51, 51)',
                  borderColor: 'rgb(51, 51, 51)',
                  fontFamily: 'RobotoMono, Arial, sans-serif',
                  fontSize: 16,
                  fontWeight: 400,
                }}
                href={reportHref}
              >
                Back to report
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </a>
            </div>
          </div>
        </section>

        {primaryAreaGroups.length === 0 && fallbackGroups.length === 0 && (
          <div className="rounded-xl border border-gray-300 p-8">
            <h2
              className="text-[28px] font-bold leading-tight"
              style={{ fontFamily: 'Clancy, Roboto, Arial, sans-serif' }}
            >
              No course recommendations available
            </h2>
            <p className="mt-3 text-[15px] leading-7 text-gray-700">
              No matching course pages were found for competences or areas below
              the configured threshold.
            </p>
          </div>
        )}

        {(primaryAreaGroups.length > 0 || fallbackGroups.length > 0) && (
          <section>
            <div className="py-5">
              <h2
                className="text-[32px] font-bold leading-tight text-black"
                style={{ fontFamily: 'Clancy, Roboto, Arial, sans-serif' }}
              >
                Recommended courses
              </h2>
            </div>

            <div className="rounded-xl border border-gray-300 p-8">
              {fallbackGroups.map((group) => (
                <div key={group.areaCode} className="p-6">
                  <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                    <h3
                      className="text-[24px] font-bold leading-tight"
                      style={{ fontFamily: 'Clancy, Roboto, Arial, sans-serif' }}
                    >
                      Area {group.areaCode} {group.areaTitle}
                    </h3>
                    <div
                      className="text-[13px] text-gray-700"
                      style={{ fontFamily: 'RobotoMono, Arial, sans-serif' }}
                    >
                      Score {group.score.toFixed(2)} · {group.estimatedLevel}
                    </div>
                  </div>

                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    {group.courses.map((course) => (
                      <a
                        key={course.url}
                        className="rounded-lg border border-gray-200 p-5 transition hover:border-gray-300"
                        href={course.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <div className="flex items-center justify-between gap-4">
                          <h4 className="text-[15px] font-semibold leading-snug text-black">
                            {course.title || course.url}
                          </h4>
                          {course.pageType && (
                            <span
                              className="border border-gray-300 bg-yellow-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-black"
                              style={{ fontFamily: 'RobotoMono, Arial, sans-serif' }}
                            >
                              {course.pageType}
                            </span>
                          )}
                        </div>
                        {course.description && (
                          <p className="mt-2 text-[13px] leading-6" style={{ color: 'rgb(24, 23, 22)' }}>
                            {course.description}
                          </p>
                        )}
                        <p className="mt-3 break-all text-[13px]" style={{ color: '#6b7280' }}>
                          {course.url}
                        </p>
                        <p className="mt-2 text-[12px]" style={{ color: '#6b7280' }}>
                          Tagged competences:{' '}
                          {course.competenceCodes.join(', ') || 'None'}
                        </p>
                      </a>
                    ))}
                  </div>
                </div>
              ))}

              {primaryAreaGroups.map((area) => (
                <div key={area.areaCode} className="p-6">
                  <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                    <h3
                      className="text-[24px] font-bold leading-tight"
                      style={{ fontFamily: 'Clancy, Roboto, Arial, sans-serif' }}
                    >
                      Area {area.areaCode} {area.areaTitle}
                    </h3>
                    <div
                      className="text-[13px] text-gray-700"
                      style={{ fontFamily: 'RobotoMono, Arial, sans-serif' }}
                    >
                      Score {Math.min(...area.competences.map((c) => c.score)).toFixed(2)}
                    </div>
                  </div>

                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    {area.competences.flatMap((group) =>
                      group.courses.map((course) => (
                        <a
                          key={course.url}
                          className="rounded-lg border border-gray-200 p-5 transition hover:border-gray-300"
                          href={course.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <div className="flex items-center justify-between gap-4">
                            <h4 className="text-[15px] font-semibold leading-snug text-black">
                              {course.title || course.url}
                            </h4>
                            {course.pageType && (
                              <span
                                className="border border-gray-300 bg-yellow-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-black"
                                style={{ fontFamily: 'RobotoMono, Arial, sans-serif' }}
                              >
                                {course.pageType}
                              </span>
                            )}
                          </div>
                          {course.description && (
                            <p className="mt-2 text-[13px] leading-6" style={{ color: 'rgb(24, 23, 22)' }}>
                              {course.description}
                            </p>
                          )}
                          <p className="mt-3 break-all text-[13px]" style={{ color: '#6b7280' }}>
                            {course.url}
                          </p>
                          <p className="mt-2 text-[12px]" style={{ color: '#6b7280' }}>
                            Tagged competences:{' '}
                            {course.competenceCodes.join(', ') || 'None'}
                          </p>
                        </a>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

      </div>
    </div>
  )
}

export default AssessmentReportCourses
